<template>
    <div class="pl-4 pt-3 row nav-padding"
         v-loading="loading">
        <el-form class="w-full"
                 label-width="130px"
                 label-position="top"
                 ref="restore_numbers"
                 :model="restore_numbers"
                 :rules="form_rules">

            <div class="row mb-3">
                <div class="col-12">
                    <el-alert
                        title="How to use"
                        type="success">
                        <template v-slot>
                            <ul>
                                <li>First select a company</li>
                                <li>Select multiple deleted lines or phone numbers to restore them</li>
                                <li>Lines or phone numbers that were deleted more than 30 days ago are not eligible to be restored</li>
                            </ul>
                        </template>
                    </el-alert>
                </div>
            </div>

            <div class="row">
                <div class="col-9">
                    <el-form-item label="Company"
                                  prop="company_id">
                        <company-auto-suggest placeholder="Search for the company to restore a resource for"
                                              v-model="restore_numbers.company_id"
                                              @input="fetchCampaigns">
                        </company-auto-suggest>
                    </el-form-item>
                </div>
                <div class="col-3">
                    <el-form-item label="Resource To Restore"
                                  prop="resource_to_restore">
                        <el-switch
                            v-model="restore_numbers.restore_number"
                            active-color="#13ce66"
                            inactive-color="#13ce66"
                            active-text="Number"
                            inactive-text="Line"
                            @change="fetchCampaigns">
                        </el-switch>
                    </el-form-item>
                </div>
            </div>

            <div class="row">
                <div v-if="restore_numbers.restore_number"
                     class="col-12">
                    <el-form-item label="Line"
                                  prop="campaign_id">
                        <el-select class="w-full"
                                   v-model="restore_numbers.campaign_id"
                                   placeholder="Select a line to restore number(s) from"
                                   :disabled="campaigns.length === 0"
                                   filterable
                                   @change="fetchPhoneNumbers">
                            <el-option
                                v-for="campaign in campaigns"
                                :key="campaign.id"
                                :label="campaign.name"
                                :value="campaign.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
                <div v-if="!restore_numbers.restore_number"
                     class="col-9">
                    <el-form-item label="Line(s)"
                                  prop="campaign_ids">
                        <el-select class="w-full"
                                   v-model="restore_numbers.campaign_ids"
                                   placeholder="Select line(s) to restore"
                                   :disabled="campaigns.length === 0"
                                   multiple
                                   filterable
                                   @change="fetchPhoneNumbers">
                            <el-option
                                v-for="campaign in campaigns"
                                :key="campaign.id"
                                :label="campaign.name | removeDeletedPrefix"
                                :value="campaign.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
                <div v-if="!restore_numbers.restore_number"
                     class="col-3 mt-4">
                    <el-form-item>
                        <el-button type="success"
                                   size="small"
                                   class="w-full"
                                   @click="submitForm">
                            Restore
                        </el-button>
                    </el-form-item>
                </div>
            </div>

            <div class="row"
                 v-if="restore_numbers.restore_number">
                <div class="col-9">
                    <el-form-item label="Phone Number"
                                  prop="phone_number_ids">
                        <el-select class="w-full"
                                   v-model="restore_numbers.phone_number_ids"
                                   :placeholder="incoming_numbers.length === 0 ? 'There is no deleted number in this line.' : 'Select phone number(s) to restore'"
                                   :disabled="incoming_numbers.length === 0"
                                   multiple
                                   filterable>
                            <el-option
                                v-for="phone_number in incoming_numbers"
                                :key="phone_number.id"
                                :label="phone_number.phone_number"
                                :value="phone_number.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
                <div class="col-3 mt-4">
                    <el-form-item>
                        <el-button type="success"
                                   size="small"
                                   class="w-full"
                                   @click="submitForm">
                            Restore
                        </el-button>
                    </el-form-item>
                </div>
            </div>
        </el-form>

        <el-dialog width="50%"
                   :title="dialogTitle"
                   :visible.sync="showResultDialog"
                   @close="resetForm">
            <div class="container text-dark">
                <div class="px-2"
                     v-if="resultHasRestored">
                    <el-alert class="mb-1"
                              :title="`Restored ${restore_numbers.restore_number ? 'phone number(s)' : 'line(s)'}`"
                              type="success"
                              :closable="false">
                        <div>
                            <p class="py-1">
                                These are successfully restored {{ restore_numbers.restore_number ? 'phone number(s)' : 'line(s)' }}.
                            </p>
                            <ul>
                                <li v-for="restoredResult in restoredResults"
                                    :key="restoredResult.id">
                                    #{{ restoredResult.id }} {{ restoredResult.name }}
                                </li>
                            </ul>
                        </div>
                    </el-alert>
                </div>
                <div class="px-2"
                     :class="{'mt-4': resultHasRestored}"
                     v-if="resultHasNotDeleted">
                    <h6 class="font-weight-bold mb-0">Not Deleted</h6>
                    <div class="mb-1">
                        <small class="font-weight-light py-1">
                            These are the {{ restore_numbers.restore_number ? 'phone number' : 'line' }}(s) that are not deleted and don't need to restore.
                        </small>
                    </div>
                    <div class="break-word">
                        {{ notDeletedResult }}
                    </div>
                </div>
                <div class="px-2"
                     :class="{'mt-4': (resultHasRestored || resultHasNotDeleted)}"
                     v-if="resultHasUnRented">
                    <el-alert class="mb-1"
                              :itle="`${restore_numbers.restore_number ? 'Phone number(s)' : 'Line(s)'} not restored`"
                              type="error"
                              :closable="false">
                        <div>
                            <p class="py-1">
                                <span v-if="restore_numbers.restore_number">Phone number was not restored because it was unrented.</span>
                                <span v-else>Line was not restored because it contains unrented phone number(s).</span>
                                <br>
                                <br/>Please contact the carrier ops team to rent the number(s) back and try again.
                            </p>
                        </div>
                    </el-alert>
                    <div class="break-word">
                        <table class="table"
                               v-if="resultHasUnRented">
                            <thead>
                            <tr>
                                <th v-if="!restore_numbers.restore_number">Line</th>
                                <th>ID</th>
                                <th>Phone Number</th>
                                <th>SID</th>
                                <th>Carrier</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="phone_number in unRentedResult"
                                :key="phone_number.id">
                                <td class="w-sm pl-0"
                                    v-if="!restore_numbers.restore_number">{{ phone_number.campaign | removeDeletedPrefix }}</td>
                                <td class="w-sm pl-0">{{ phone_number.id }}</td>
                                <td class="w-sm pl-0">{{ phone_number.phone_number }}</td>
                                <td class="w-sm pl-0">{{ phone_number.sid }}</td>
                                <td class="w-sm pl-0">{{ phone_number.carrier_name | capitalize }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="row px-2">
                    <div class="col offset-10">
                        <el-button type="success"
                                   class="right"
                                   @click="showResultDialog = false">
                            Confirm
                        </el-button>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import auth from '../../auth'
import CompanyAutoSuggest from '../company-autosuggest'
import {form_validation_mixin} from '../../mixins'

export default {
    components: {
        CompanyAutoSuggest
    },

    mixins: [
        form_validation_mixin
    ],

    data() {
        return {
            loading: false,
            auth: auth,
            form_name: 'restore_numbers',
            restore_numbers: {
                restore_number: false,
                company_id: null,
                campaign_id: null,
                campaign_ids: [],
                phone_number_ids: []
            },
            form_rules: {
                phone_number_ids: {
                    required: true,
                    message: 'Select phone number(s) to restore',
                    trigger: 'blur'
                },
                company_id: {
                    required: true,
                    message: 'Select a company to restore a resource for',
                    trigger: 'blur'
                },
                campaign_id: {
                    required: true,
                    message: 'Select a line to restore phone numbers from',
                    trigger: 'blur'
                },
                campaign_ids: {
                    required: true,
                    message: 'Select line(s) to restore',
                    trigger: 'blur'
                },
            },
            showResultDialog: false,
            restore_result: [],
            campaigns: [],
            incoming_numbers: [],
            campaignCancelToken: null,
            campaignSource: null,
            incomingNumberCancelToken: null,
            incomingNumberSource: null,
        }
    },

    mounted() {
        this.campaignCancelToken = axios.CancelToken;
        this.campaignSource = this.campaignCancelToken.source();
        this.incomingNumberCancelToken = axios.CancelToken;
        this.incomingNumberSource = this.incomingNumberCancelToken.source();
    },

    computed: {
        resultHasRestored() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('success')) {
                return Object.entries(this.restore_result.success).length
            }

            return false
        },

        restoredResults() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('success')) {
                return this.restore_result.success
            }

            return []
        },

        resultHasNotDeleted() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('not_deleted')) {
                return Object.entries(this.restore_result.not_deleted).length
            }

            return false
        },

        notDeletedResult() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('not_deleted')) {
                return Object.entries(this.restore_result.not_deleted).map(([key, value]) => `#${value['id']} ${value['name']}`).join(', ')
            }

            return false
        },

        resultHasUnRented() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('un_rented')) {
                return Object.entries(this.restore_result.un_rented).length
            }

            return false
        },

        unRentedResult() {
            if (Object.keys(this.restore_result).length && this.restore_result.hasOwnProperty('un_rented')) {
                // if we are restoring lines, the `un_rented` key will contain an array of objects with the `un_rented_numbers` key in it
                // we want to loop through the `un_rented_numbers` array and return the objects in it and attach the `id` and `name` of the line to the object
                if (this.restore_numbers.restore_number === false) {
                    return this.restore_result.un_rented.map(campaign => {
                        if (!campaign.un_rented_numbers || !campaign.un_rented_numbers.length) {
                            return []
                        }
                        return campaign.un_rented_numbers.map(numbers => {
                            return {
                                ...numbers,
                                campaign: `#${campaign.id} ${campaign.name}`
                            }
                        })
                    }).flat()
                } else {
                    // otherwise it will be an array of numbers
                    return this.restore_result.un_rented
                }
            }

            return []
        },

        dialogTitle() {
            let title = 'Restore '

            if (this.restore_numbers.restore_number) {
                title += ' Phone Numbers'
            } else {
                title += ' Lines'
            }

            return title
        }
    },

    methods: {
        resetForm() {
            this.loading = false
            this.restore_numbers = {
                restore_number: this.restore_numbers.restore_number,
                company_id: this.restore_numbers.company_id,
                campaign_id: null,
                campaign_ids: [],
                phone_number_ids: []
            }

            this.campaigns = []
            this.incoming_numbers = []
            this.fetchCampaigns()
        },

        submitForm() {
            if (this.validateForm(this.form_name)) {
                this.loading = true
                const admin_id = this.auth?.user?.profile?.id || null

                let url = `/api/admin/campaigns/${admin_id}/restore`
                let params = {
                    company_id: this.restore_numbers.company_id,
                    campaign_ids: this.restore_numbers.campaign_ids,
                }
                // do we need to restore lines or numbers?
                if (this.restore_numbers.restore_number) {
                    url = `/api/admin/campaigns/${admin_id}/incoming-numbers/restore`
                    params = {
                        company_id: this.restore_numbers.company_id,
                        campaign_id: this.restore_numbers.campaign_id,
                        incoming_number_ids: this.restore_numbers.phone_number_ids,
                    }
                }

                axios.post(url, params)
                    .then(res => {
                        this.restore_result = res.data
                        this.showResultDialog = true
                    })
                    .catch(err => {
                        this.loading = false

                        return this.$notify.error({
                            title: 'Restore failed',
                            message: err.response.data.message,
                        });
                    })
            }
        },

        fetchCampaigns() {
            this.form_rules.campaign_id.required = this.restore_numbers.restore_number
            this.form_rules.campaign_ids.required = !this.form_rules.campaign_id.required
            if (this.restore_numbers.company_id == null) {
                return
            }

            this.campaignSource.cancel('fetchCampaigns canceled by the user.')
            this.campaignSource = this.campaignCancelToken.source()

            this.campaigns = []
            this.restore_numbers.campaign_id = null
            this.restore_numbers.campaign_ids = []
            this.restore_numbers.phone_number_ids = []

            axios.get(`/api/admin/company/${this.restore_numbers.company_id}/campaigns`, {
                params: {
                    deleted: !this.restore_numbers.restore_number,
                },
                cancelToken: this.campaignSource.token
            })
                .then(res => {
                    this.campaigns = res.data
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        // console.log('Request canceled', err.message)
                    } else {
                        this.$notify.error({
                            title: 'Error searching for campaigns',
                            message: err.response.data.message,
                        });
                    }
                })
        },

        fetchPhoneNumbers() {
            if (this.restore_numbers.company_id == null) {
                return
            }

            // if we are restoring lines don't bother fetching numbers
            if (!this.restore_numbers.restore_number) {
                return
            }

            this.incomingNumberSource.cancel('fetchPhoneNumbers canceled by the user.')
            this.incomingNumberSource = this.incomingNumberCancelToken.source()

            this.restore_numbers.phone_number_ids = []

            axios.get(`/api/admin/company/${this.restore_numbers.company_id}/campaigns/${this.restore_numbers.campaign_id}/incoming-numbers`, {
                params: {
                    deleted: true,
                },
                cancelToken: this.incomingNumberSource.token
            })
                .then(res => {
                    this.incoming_numbers = res.data
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        // console.log('Request canceled', err.message)
                    } else {
                        this.$notify.error({
                            title: 'Error searching for phone numbers',
                            message: err.response.data.message,
                        });
                    }
                })
        },
    },

    filters: {
        removeDeletedPrefix(value) {
            return value.replaceAll(new RegExp("_deleted_\\d*", "g"), '')
        }
    }
}
</script>
